import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPin, CaretRight, CaretDown, ArrowClockwise, MagnifyingGlass, Package, Receipt, Buildings } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const th = { textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid #E2E8F0", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", fontSize: 13, color: "#0F172A", borderBottom: "1px solid #F1F5F9", verticalAlign: "top" };
const pill = (bg, fg, bd) => ({ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, color: fg, background: bg, border: `1px solid ${bd}`, whiteSpace: "nowrap", display: "inline-block" });
const usagePill = (usageCode) => {
  if (usageCode === "SHIP_TO") return pill("#DBEAFE", "#1E40AF", "#BFDBFE");
  if (usageCode === "BILL_TO") return pill("#DCFCE7", "#166534", "#BBF7D0");
  return pill("#F1F5F9", "#475569", "#E2E8F0");
};

// One customer's expanded address book (lazy-loaded on expand).
function AddressBook({ code }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = (refresh = false) => {
    setLoading(true); setErr("");
    api.get(`/sap/customers/${encodeURIComponent(code)}/addresses${refresh ? "?refresh=1" : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load addresses from SAP"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [code]);

  if (loading) return <div data-testid={`addr-loading-${code}`} style={{ padding: 18, color: "#94A3B8", fontSize: 13 }}>Loading live from SAP…</div>;
  if (err) return <div data-testid={`addr-error-${code}`} style={{ padding: 18, color: "#B91C1C", fontSize: 13 }}>{err}</div>;
  if (!data || (data.counts?.total || 0) === 0)
    return <div data-testid={`addr-empty-${code}`} style={{ padding: 18, color: "#94A3B8", fontSize: 13 }}>No addresses found in SAP for account <b>{data?.account_id || code}</b>.</div>;

  const Section = ({ title, rows, icon: Icon }) => rows.length === 0 ? null : (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>
        <Icon size={15} weight="bold" /> {title} <span style={{ color: "#94A3B8", fontWeight: 700 }}>({rows.length})</span>
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {rows.map((a, i) => (
          <div key={a.uuid || i} data-testid={`addr-row-${code}-${a.usage_code}-${i}`}
               style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px" }}>
            <span style={usagePill(a.usage_code)}>{a.usage}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{a.formatted || `${a.city} ${a.region} ${a.postal} ${a.country}`}</div>
              {(a.street || a.street2) && <div style={{ fontSize: 11.5, color: "#64748B" }}>{[a.street, a.street2].filter(Boolean).join(", ")}</div>}
              <div style={{ fontSize: 10.5, color: "#94A3B8", fontFamily: "Inter, sans-serif", marginTop: 2 }}>UUID {a.uuid || "—"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid={`addr-book-${code}`} style={{ padding: "14px 16px", background: "#F8FAFC" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 12.5, color: "#475569" }}>
          SAP account <b>{data.account_id}</b> · {data.counts.total} address{data.counts.total === 1 ? "" : "es"}
          <span style={{ marginLeft: 8, color: "#1E40AF", fontWeight: 700 }}>{data.counts.ship_to} Ship-To</span>
          <span style={{ marginLeft: 8, color: "#166534", fontWeight: 700 }}>{data.counts.bill_to} Bill-To</span>
          {data.counts.other > 0 && <span style={{ marginLeft: 8, color: "#64748B", fontWeight: 700 }}>{data.counts.other} Other</span>}
        </div>
        <button data-testid={`addr-refresh-${code}`} onClick={() => load(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", border: "1px solid #CBD5E1", borderRadius: 7, background: "#fff", color: "#334155", fontWeight: 700, fontSize: 11.5, cursor: "pointer" }}>
          <ArrowClockwise size={13} weight="bold" /> Re-pull from SAP
        </button>
      </div>
      <Section title="Ship-To (Consignee / Deliver-to)" rows={data.ship_to} icon={Package} />
      <Section title="Bill-To" rows={data.bill_to} icon={Receipt} />
      <Section title="Other / Default" rows={data.other} icon={Buildings} />
    </div>
  );
}

export default function CustomerAddressExplorer() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/sap/customers/address-roster")
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => toast.error("Failed to load customer roster"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => `${r.app_code} ${r.name} ${r.sap_account}`.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="customer-address-explorer">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <MapPin size={26} weight="duotone" color="#2563EB" />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>Customer Address Explorer</h1>
        </div>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 16px" }}>
          Read-only view of every mapped customer's <b>Ship-To</b> and <b>Bill-To</b> addresses, pulled <b>live from SAP ByDesign</b>.
          Expand a customer to see its address book (nothing is written back).
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, maxWidth: 380 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <MagnifyingGlass size={15} weight="bold" style={{ position: "absolute", left: 10, top: 10, color: "#94A3B8" }} />
            <input data-testid="addr-explorer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, name or SAP account…"
              style={{ width: "100%", padding: "8px 10px 8px 32px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 }} />
          </div>
        </div>

        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 34 }}></th>
                <th style={th}>App Code</th>
                <th style={th}>Customer</th>
                <th style={th}>SAP Account</th>
                <th style={th}>Sales Unit</th>
                <th style={th}>Push Config</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 30, color: "#94A3B8" }}>Loading customers…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 30, color: "#94A3B8" }}>No customers match.</td></tr>}
              {!loading && filtered.map((r) => {
                const open = expanded === r.app_code;
                return (
                  <>
                    <tr key={r.app_code} data-testid={`addr-cust-row-${r.app_code}`} onClick={() => setExpanded(open ? null : r.app_code)}
                        style={{ cursor: "pointer", background: open ? "#F8FAFC" : "#fff" }}>
                      <td style={td}>{open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}</td>
                      <td style={{ ...td, fontWeight: 700, fontFamily: "Inter, sans-serif" }}>{r.app_code}</td>
                      <td style={td}>{r.name}</td>
                      <td style={{ ...td, fontFamily: "Inter, sans-serif" }}>{r.sap_account}</td>
                      <td style={td}>{r.sales_unit || <span style={{ color: "#CBD5E1" }}>—</span>}</td>
                      <td style={td}>{r.configured
                        ? <span style={pill("#DCFCE7", "#166534", "#BBF7D0")}>Configured</span>
                        : <span style={pill("#FEF3C7", "#92400E", "#FDE68A")}>Not set</span>}</td>
                    </tr>
                    {open && (
                      <tr key={`${r.app_code}-book`}>
                        <td colSpan={6} style={{ padding: 0, borderBottom: "1px solid #E2E8F0" }}>
                          <AddressBook code={r.app_code} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
